import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css'; // You can add custom styles here
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';

const baseURL = process.env.REACT_APP_BACKEND_API_URL;

function loadGtag() {
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=G-C1ZPG2613G`;
  script.async = true;
  document.head.appendChild(script);

  script.onload = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-C1ZPG2613G');
  };
}

function App() {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isQuestionSubmitted, setIsQuestionSubmitted] = useState(false); 
  const [openQuestion, setOpenQuestion] = useState(null);
  const location = useLocation(); // For accessing query params
  const navigate = useNavigate(); // For navigation

  // Function to get query parameters from the URL
  const getQueryParams = React.useCallback((param) => {
    return new URLSearchParams(location.search).get(param);
  }, [location]);

  useEffect(() => {
    // Check for qId in URL and load the corresponding response
    const qId = new URLSearchParams(location.search).get('qId');
    if (qId) {
      // Make POST request to /load with qId
      axios.post(`${baseURL}/load`, { qId })
        .then((res) => {
          if (res.data.found) {
            // Set the question and response with the data returned by the server
            setQuestion(res.data.question);
            setResponse({
              trump: res.data.trump,
              harris: res.data.harris,
            });
            setIsButtonDisabled(true); // Disable the question input
            setIsQuestionSubmitted(true); // Show 'Ask New Question' button
          } else {
            // If not found, clear the qId from the URL and reset state
            navigate(window.location.pathname, { replace: true });
            setQuestion('');
            setResponse(null);
          }
        })
        .catch((err) => {
          console.error("Error loading response with qId:", err);
          // Clear qId from URL and reset state if there's an error
          navigate(window.location.pathname, { replace: true });
          setQuestion('');
          setResponse(null);
        });
    }
  }, [location, navigate, getQueryParams]); // Run this effect when the location (URL) changes

  useEffect(() => {
    // Set the title dynamically
    document.title = "Ask Trump & Harris Anything"; // Set your desired title here
  }, []); // The empty array ensures this runs only once when the component mounts

  useEffect(() => {
    // Load Google Analytics script dynamically
    loadGtag();
  }, []);
  
  const handleSubmit = async () => {
    if (!question.trim()) return; // Avoid empty submissions
    setIsButtonDisabled(true);
    setIsQuestionSubmitted(true);

    try {
      const result = await axios.post(`${baseURL}/chat`, { question });
      setResponse(result.data);
      if (result.data.qId) {
        const newUrl = `${window.location.pathname}?qId=${result.data.qId}`;
        window.history.replaceState(null, '', newUrl); 
      }
    } catch (error) {
      console.error('Error asking the question:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleAskNewQuestion = () => {
    // Refresh the page and clear qId from URL
    navigate(window.location.pathname, { replace: true });
    setQuestion('');
    setResponse(null);
    setIsButtonDisabled(false);
    setIsQuestionSubmitted(false);
  };

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const currentUrl = encodeURIComponent(window.location.href); // Get current page URL and encode it
  const shareText = encodeURIComponent("Check out how Trump and Harris (AI) might have answered: " + question.trim()); // Text to share

  return (
    <div className="app">
      <h1 className="title">Ask Trump & Harris Anything</h1>
      <p><small><i>Disclaimer: Responses are AI-generated based on candidates' recent speeches, campaign websites and other publicly available materials. These are not actual statements or endorsements. For more info, see FAQ below.</i></small></p>
      <textarea
        className="question-box"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Type your question here..."
        onKeyPress={handleKeyPress}
        disabled={isButtonDisabled}
        style={{ color: 'green' }}
      />
      <button
        className="ask-button"
        onClick={isQuestionSubmitted ? handleAskNewQuestion : handleSubmit}
        disabled={isButtonDisabled && !response}
        style={{ backgroundColor: isButtonDisabled && !response ? 'grey' : '' }}
      >
        {isButtonDisabled && !response ? "Please wait for a few seconds, generating response..." : isQuestionSubmitted ? "Ask New Question" : "Ask"}
      </button>

      {response && (
        <>
      <div className="share-section" style={{ display: 'flex', alignItems: 'center' }}>
      <p style={{ marginRight: '10px' }}>Share this question and responses:</p>
            <a href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${shareText}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/x.png" alt="Share on X" style={{ width: '30px', marginRight: '10px' }} />
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}&quote=${shareText}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/facebook.png" alt="Share on Facebook" style={{ width: '30px', marginRight: '10px' }} />
            </a>
            <a href={`https://api.whatsapp.com/send?text=${shareText}%20${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/whatsapp.png" alt="Share on WhatsApp" style={{ width: '30px', marginRight: '10px' }} />
            </a>
            <a href={`https://slack.com/intl/en-in/share?text=${shareText}&url=${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/slack.png" alt="Share on Slack" style={{ width: '30px', marginRight: '10px' }} />
            </a>
            <a href={`https://www.messenger.com/t/?link=${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/fb_messenger.png" alt="Share on Facebook Messenger" style={{ width: '30px', marginRight: '10px' }} />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/icons/linkedin.png" alt="Share on LinkedIn" style={{ width: '30px' }} />
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(decodeURIComponent(currentUrl));
                alert('URL copied to clipboard!');
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '10px' }}
            >
              <img src="/icons/clipboard.png" alt="Copy URL" style={{ width: '30px' }} />
            </button>
      </div>
        <div className="response-section">
          <div className="column">
            <h2>Trump (AI)</h2>
            <ReactMarkdown className="response-text trump-text">
              {response.trump}
            </ReactMarkdown>
          </div>
          <div className="column">
            <h2>Harris (AI)</h2>
            <ReactMarkdown className="response-text harris-text">
              {response.harris}
            </ReactMarkdown>
          </div>
        </div>
        </>
      )}

      <footer className="footer">
        <p><b>Disclaimer:</b> The responses provided on this website are generated by AI models trained on publicly available data, including recent campaign speeches, campaign websites, and other relevant materials. While these responses are simulations and generally accurate, users should independently verify facts. These responses should not be considered actual statements or opinions of the individuals they portray. This site is for informational purposes only and does not represent any endorsements, affiliations, or official communications from the individuals or their respective campaigns.</p>
      </footer>

      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        
        {/* Question 1 */}
        <div className="faq-item">
          <div 
            className="faq-question" 
            onClick={() => toggleQuestion(1)} 
            style={{ cursor: 'pointer' }} // Change cursor to pointer
          >
            <span className="faq-arrow">{openQuestion === 1 ? 'v' : '>'}</span>
            <h3>How are the responses generated?</h3>
          </div>
          {openQuestion === 1 && (
            <div className="faq-answer">
              <p>A large language model (LLM) generates these responses. The model is pre-trained on a broad corpus of knowledge. A version of the model is then tuned for each candidate, based on content from the candidates' political speeches, campaign websites, and other public statements. The models are then prompted with some key facts about the upcoming election, as well as the candidates' stated policy agendas and their biographies.</p>
              <p>For the technically curious, I used Google’s Gemini 1.5 Flash as the base model and fine-tuned a separate model for each candidate using synthetic Q&A pairs from their speeches and policy agendas. Fine-tuning ensures the models accurately simulate each candidate's voice, tone, and style, and respond to common questions as they would. I'll continue improving this project as the election approaches, and suggestions are welcome. Note: this is a side project, and I limited development time to just a few hours.</p>
            </div>
          )}
        </div>

        {/* Question 2 */}
        <div className="faq-item">
          <div 
            className="faq-question" 
            onClick={() => toggleQuestion(2)} 
            style={{ cursor: 'pointer' }} // Change cursor to pointer
          >
            <span className="faq-arrow">{openQuestion === 2 ? 'v' : '>'}</span>
            <h3>Can I rely on these responses for factual information?</h3>
          </div>
          {openQuestion === 2 && (
            <div className="faq-answer">
              <p>While the responses are generally accurate, they may not always be completely reliable. Users should independently verify any factual information, as the responses should not be considered official statements or endorsements.</p>
            </div>
          )}
        </div>

        {/* Question 3 */}
        <div className="faq-item">
          <div 
            className="faq-question" 
            onClick={() => toggleQuestion(3)} 
            style={{ cursor: 'pointer' }} // Change cursor to pointer
          >
            <span className="faq-arrow">{openQuestion === 3 ? 'v' : '>'}</span>
            <h3>Is this website affiliated with any political campaigns?</h3>
          </div>
          {openQuestion === 3 && (
            <div className="faq-answer">
              <p>No, this website is independently created and not affiliated with any political campaign or candidate.</p>
            </div>
          )}
        </div>

        {/* Question 4 */}
        <div className="faq-item">
          <div 
            className="faq-question" 
            onClick={() => toggleQuestion(4)} 
            style={{ cursor: 'pointer' }} // Change cursor to pointer
          >
            <span className="faq-arrow">{openQuestion === 4 ? 'v' : '>'}</span>
            <h3>What was the source material used?</h3>
          </div>
          {openQuestion === 4 && (
            <div className="faq-answer">
              <p>I used a selection of recent political speeches from both candidates, along with content from their campaign websites and speeches delivered at their respective national conventions.</p>
            </div>
          )}
        </div>

        {/* Question 5 */}
        <div className="faq-item">
          <div 
            className="faq-question" 
            onClick={() => toggleQuestion(5)} 
            style={{ cursor: 'pointer' }} // Change cursor to pointer
          >
            <span className="faq-arrow">{openQuestion === 5 ? 'v' : '>'}</span>
            <h3>Why did you create this?</h3>
          </div>
          {openQuestion === 5 && (
            <div className="faq-answer">
              <p>I built this as a social experiment because I’m fascinated by how AI has become such a regular part of our lives, and how much it is impacting our public discourse. How well can we predict what people might say, given what they have said in the past?</p>
            </div>
          )}
        </div>

      {/* Question 6 */}
      <div className="faq-item">
        <div 
          className="faq-question" 
          onClick={() => toggleQuestion(6)} 
          style={{ cursor: 'pointer' }} // Change cursor to pointer
        >
          <span className="faq-arrow">{openQuestion === 6 ? 'v' : '>'}</span>
          <h3>Other questions / suggestions? Just want to get in touch?</h3>
        </div>
        {openQuestion === 6 && (
          <div className="faq-answer">
            <p>Please send me an email at <b>cutouts-07.gazebos AT icloud.com</b>. Or connect with me on <a href='https://www.linkedin.com/in/ycmak/'>LinkedIn</a>.</p>
            <p>I welcome all feedback and suggestions, as well as opportunities for collaboration. I also welcome sponsors or donations to keep this page running or to make it better/faster.</p>
          </div>
        )}
      </div>
    </div>
  </div>
      

  );
}

export default App;
